import { Tab as TabBase } from '@mui/material';
import { memo, useMemo } from 'react';
import * as SX from './sx';
import { TabLabel } from './tab-label';
import type { TabProps as Props } from './types';

export const Tab = memo(
  ({
    className,
    LabelContent,
    LabelVariant,
    label,
    selected,
    textColor,
    ...tabProps
  }: Props) => {
    // Memoize the color calculation to prevent recalculation on every render
    const color = useMemo(
      () => (selected ? textColor : 'default'),
      [selected, textColor]
    );

    // Memoize the props object to prevent recreation on every render
    const props = useMemo(
      () => ({ ...tabProps, selected, textColor }),
      [tabProps, selected, textColor]
    );

    // Memoize the badge element to prevent recreation when other props change
    const labelElement = useMemo(
      () =>
        LabelContent ? (
          <TabLabel
            disabled={tabProps.disabled}
            variant={LabelVariant ?? 'filled'}
            color={color}
          >
            {LabelContent}
          </TabLabel>
        ) : null,
      [LabelContent, tabProps.disabled, LabelVariant, color]
    );

    // Memoize the label element to prevent recreation when other props change
    const tabLabel = useMemo(
      () => (
        <>
          {label}
          {labelElement}
        </>
      ),
      [label, labelElement]
    );

    // Memoize the final props passed to TabBase to prevent unnecessary re-renders
    const finalProps = useMemo(
      () => ({
        sx: SX.root,
        label: tabLabel,
        ...props,
      }),
      [tabLabel, props]
    );

    return <TabBase {...finalProps} />;
  }
);

Tab.displayName = 'Tab';
